import React, {
    ChangeEvent,
    FunctionComponent,
    useEffect,
    useState
} from 'react';
import {
    Formik,
    FormikHelpers,
    FormikProps,
    Field,
    Form as FormikForm,
    useFormikContext,
} from 'formik';
import DOMPurify from 'dompurify';

// MUI
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Pagination from '@mui/material/Pagination';

// Common
import api from '../../../../api';
import { GENERAL } from '../../../../constants';
import { models, types } from '../../../../types';

// Components
import { Modal } from '../../../Modal';
import { FormTextField } from '../FormInputs';

// Properties
import { validationSchema, defaultValues } from './ModalSchool.validation';
import * as styles from './ModalSchool.styles';

interface ProfileFields {
    schoolId: any;
}

type TProps = {
    open: boolean;
    onClose: () => void;
    setSchoolDetails: (school: types.TSchool) => void;
}

const ModalSchool: FunctionComponent<TProps> = ({
    open,
    onClose,
    setSchoolDetails,
}) => {
    const [ schools, setSchools ] = useState<any[]>([]);
    const [ currentPage, setCurrentPage ] = useState(1);
    const [ totalPages, setTotalPages ] = useState(0);

    const { values: { schoolId }, setFieldValue } = useFormikContext<ProfileFields>();

    const fetchSchools = (model: models.TSchools) => {
        api.getSchools(model)
            .then(({ data }) => {
                setSchools(data.data);
                setCurrentPage(data.meta.page);
                setTotalPages(data.meta.totalPages);
            });
    };

    useEffect(() => {
        const model: models.TSchools = {
            page: currentPage,
        };
        fetchSchools(model);
        // eslint-disable-next-line
    }, []);

    const handleChangePage = (event: ChangeEvent<unknown>, page: number, values: models.TSchools) => {
        const model: models.TSchools = {
            page: page,
            city: values.city,
            street: values.street,
            zipCode: values.zipCode,
        };
        fetchSchools(model);
    };

    const handleSubmit = (values: models.TSchools) => {
        const model: models.TSchools = {
            page: currentPage,
            city: values.city,
            street: values.street,
            zipCode: values.zipCode,
        };
        fetchSchools(model);
    };

    const handleSelectSchool = (school: types.TSchool) => {
        setSchoolDetails(school);
        setFieldValue('schoolId', school.id);
        setFieldValue('schoolLevel', school.level);
        setFieldValue('programId', '');
        setFieldValue('subjectId', '');
        onClose();
    };

    return (
        schools && (
            <Modal
                open={open}
                onClose={onClose}
                title={GENERAL.SCHOOL_MODAL_TITLE}
            >
                <Formik
                    initialValues={defaultValues}
                    validationSchema={validationSchema}
                    onSubmit={(
                        values: models.TSchools,
                        formikHelpers: FormikHelpers<models.TSchools>
                    ) => {
                        handleSubmit(values);
                        formikHelpers.setSubmitting(false);
                    }}
                >
                    {(formikProps: FormikProps<models.TSchools>) => (
                        <>
                            <FormikForm noValidate autoComplete="off">
                                <Box sx={styles.form}>
                                    <Field
                                        name="city"
                                        label={GENERAL.SCHOOL_MODAL_CITY}
                                        className="school-inputs school-address"
                                        component={FormTextField}
                                    />
                                    <Field
                                        name="street"
                                        label={GENERAL.SCHOOL_MODAL_STREET}
                                        className="school-inputs school-address"
                                        component={FormTextField}
                                    />
                                    <Field
                                        name="zipCode"
                                        label={GENERAL.SCHOOL_MODAL_ZIP}
                                        inputProps={{ maxLength: 6 }}
                                        className="school-inputs school-zipCode"
                                        component={FormTextField}
                                    />
                                    <Button
                                        variant="contained"
                                        type="submit"
                                        disabled={formikProps.isSubmitting}
                                        sx={styles.submit}
                                    >
                                        {GENERAL.SCHOOL_MODAL_SUBMIT_BUTTON}
                                    </Button>
                                </Box>
                                <Typography variant="subtitle2" sx={styles.text}>
                                    {GENERAL.SCHOOL_MODAL_DESCRIPTION}
                                </Typography>
                            </FormikForm>
                            {
                                schools.map((school: types.TSchool) => (
                                    <Box key={school.id} sx={styles.school}>
                                        <Typography
                                            variant="h6"
                                            sx={styles.schoolTitle}
                                        >
                                            {school.name}
                                        </Typography>
                                        <Box sx={styles.schoolBox}>
                                            <Typography
                                                variant="body2"
                                                dangerouslySetInnerHTML={{__html: DOMPurify.sanitize(school.address.replace(', ', '<br />'))}}
                                                sx={styles.schoolText}
                                            />
                                            <Button
                                                variant="contained"
                                                disabled={schoolId === school.id}
                                                onClick={() => handleSelectSchool(school)}
                                            >
                                                {GENERAL.SCHOOL_MODAL_SELECT_BUTTON}
                                            </Button>
                                        </Box>
                                    </Box>
                                ))
                            }
                            {
                                totalPages > 1 && (
                                    <Box sx={styles.pagination}>
                                        <Typography
                                            variant="body2"
                                            sx={styles.paginationText}
                                        >
                                            {GENERAL.SCHOOL_MODAL_PAGE}
                                        </Typography>
                                        <Pagination
                                            hidePrevButton
                                            hideNextButton
                                            count={totalPages}
                                            page={currentPage}
                                            siblingCount={2}
                                            boundaryCount={0}
                                            onChange={(event, page) => handleChangePage(event, page, formikProps.values)}
                                        />
                                    </Box>
                                )
                            }
                        </>
                    )}
                </Formik>
            </Modal>
        )
    );
};

export default ModalSchool;
